import {
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Download,
  FileDown,
  FileText,
  Lock,
  RefreshCw,
  Receipt,
  Users,
  TrendingUp,
  ListChecks,
} from 'lucide-react';
import { DataTable, NoOpsSelected, PageError, PageLoading } from '../../../../components';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Tabs, TabsContent, PageTabsList, PageTabsTrigger } from '../../../../components/ui/tabs';
import { formatCurrencyBrlFromCents } from '../../../../utils';
import { useEntregoFechamento } from './hooks/use-entrego-fechamento';

export function EntregoFechamento() {
  const {
    currentOps,
    data,
    summaryStats,
    loading,
    isFetching,
    error,
    resumoColumns,
    operacoesColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    loadData,
    handleRequestNFSZip,
    handleCheckNFSZip,
    nfsZipLoading,
    nfsZipJobKey,
    nfsZipChecking,
    nfsZipError,
    nfsZipReset,
    weekOptions,
    selectedSeconda,
    setSelectedSeconda,
    selectedWeekOption,
    isCurrentWeek,
    nfsData,
    nfsLoading,
    nfsFetching,
    nfsError,
    srSemaforoStatus,
    srData,
    srColumns,
    handleDownloadSR,
    srLoading,
    srFetching,
    srError,
  } = useEntregoFechamento();

  if (!currentOps) {
    return (
      <NoOpsSelected message="Selecione uma operação para visualizar o fechamento semanal." />
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-[hsl(210,50%,10%)]">
            Fechamento Semanal
          </h1>
          <p className="text-sm text-[hsl(215,16%,47%)]">
            Consulte as operações fechadas por semana para a ops{' '}
            <span className="font-medium text-[hsl(215,16%,30%)]">{currentOps}</span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadData()}
          disabled={isFetching}
          className="flex items-center gap-2 shrink-0 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Seletor de semana */}
      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-[hsl(210,50%,10%)]">
              <CalendarClock className="h-4 w-4 text-[hsl(215,16%,47%)]" />
              Semana:
            </div>
            <Select value={selectedSeconda} onValueChange={setSelectedSeconda}>
              <SelectTrigger className="w-full sm:w-72 border-[hsl(214,32%,91%)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {weekOptions.map((opt) => (
                  <SelectItem key={opt.segunda} value={opt.segunda}>
                    <span className={opt.isCurrentWeek ? 'font-semibold text-amber-700' : ''}>
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Badge de status ao lado do seletor */}
            {data && (
              <Badge
                variant={data.semanaCompleta ? 'default' : 'destructive'}
                className={
                  data.semanaCompleta
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shrink-0'
                    : 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100 shrink-0'
                }
              >
                {data.semanaCompleta ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Semana fechada
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {isCurrentWeek ? 'Em andamento' : 'Semana incompleta'}
                  </span>
                )}
              </Badge>
            )}
          </div>

          {/* Período exibido */}
          {selectedWeekOption && (
            <p className="mt-2 text-xs text-[hsl(215,16%,47%)]">
              De{' '}
              <span className="font-medium">{selectedWeekOption.segunda}</span>
              {' '}até{' '}
              <span className="font-medium">{selectedWeekOption.domingo}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Banner semana atual — aparece antes mesmo dos dados carregarem */}
      {isCurrentWeek && (
        <Alert className="border-amber-400 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 font-semibold">
            Você está visualizando a semana atual — dados incompletos
          </AlertTitle>
          <AlertDescription className="text-amber-700">
            A semana ainda está em andamento. Os dados refletem apenas as operações realizadas até
            hoje. O arquivo exportado conterá{' '}
            <code className="font-mono bg-amber-100 px-1 rounded text-xs">SEMANA_NAO_FECHADA</code>{' '}
            no nome.
          </AlertDescription>
        </Alert>
      )}

      <PageError error={error} />
      <PageLoading loading={loading} />

      {!loading && data && (
        <>
          {/* Aviso de dias faltando em semana passada (não é a semana atual) */}
          {!isCurrentWeek && !data.semanaCompleta && (
            <Alert className="border-red-300 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-700 font-semibold">
                Semana com dias sem fechamento
              </AlertTitle>
              <AlertDescription className="text-red-600 space-y-2">
                <p>Os seguintes dias não possuem registros de operações compradas ou liquidadas:</p>
                {data.diasFaltantes.length > 0 && (
                  <ul className="list-disc list-inside space-y-0.5 text-sm pl-1">
                    {data.diasFaltantes.map((d) => (
                      <li key={`${d.ops}-${d.date}`}>
                        <strong>{d.ops}</strong> — {d.date}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-sm font-medium">
                  Arquivos exportados conterão{' '}
                  <code className="font-mono bg-red-100 px-1 rounded text-xs">
                    SEMANA_NAO_FECHADA
                  </code>{' '}
                  no nome.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Aviso de dias faltando na semana atual */}
          {isCurrentWeek && !data.semanaCompleta && data.diasFaltantes.length > 0 && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-700 font-semibold">
                Dias sem operações até agora
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                <ul className="list-disc list-inside space-y-0.5 text-sm mt-1 pl-1">
                  {data.diasFaltantes.map((d) => (
                    <li key={`${d.ops}-${d.date}`}>
                      <strong>{d.ops}</strong> — {d.date}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Cards de resumo */}
          {summaryStats && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Entregadores
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.totalEntregadores}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-violet-50">
                      <ListChecks className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Operações
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.totalOperacoes}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-50">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Valor Face Total
                      </p>
                      <p className="text-lg font-bold text-emerald-700 leading-tight">
                        {formatCurrencyBrlFromCents(summaryStats.valorFaceTotal)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <Receipt className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">
                        Com Comprovante
                      </p>
                      <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">
                        {summaryStats.comComprovante}
                        <span className="text-sm font-normal text-[hsl(215,16%,47%)] ml-1">
                          / {summaryStats.totalOperacoes}
                        </span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Abas das tabelas */}
          <Tabs defaultValue="resumo" className="w-full">
            <PageTabsList>
              <PageTabsTrigger value="resumo">
                <Users className="h-4 w-4" />
                Resumo por Entregador
                {data.resumo.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {data.resumo.length}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger value="operacoes">
                <FileText className="h-4 w-4" />
                Operações Detalhadas
                {data.operacoes.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {data.operacoes.length}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger
                value="nfs"
                disabled={srSemaforoStatus !== 'concluido' && process.env.REACT_APP_STAGE !== 'dev'}
              >
                {srSemaforoStatus === 'concluido' || process.env.REACT_APP_STAGE === 'dev' ? (
                  <FileDown className="h-4 w-4" />
                ) : (
                  <Lock className="h-4 w-4 text-[hsl(215,16%,60%)]" />
                )}
                NFS
                {srSemaforoStatus !== 'concluido' && process.env.REACT_APP_STAGE !== 'dev' && (
                  <span className="ml-1 text-xs text-[hsl(215,16%,60%)]">(aguardando email SR)</span>
                )}
                {(srSemaforoStatus === 'concluido' || process.env.REACT_APP_STAGE === 'dev') && nfsData && (nfsData.totalAntecipacao + nfsData.totalSaldoRemanescente) > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {nfsData.totalAntecipacao + nfsData.totalSaldoRemanescente}
                  </Badge>
                )}
              </PageTabsTrigger>
              <PageTabsTrigger
                value="sr"
                disabled={srSemaforoStatus !== 'concluido' && process.env.REACT_APP_STAGE !== 'dev'}
              >
                {srSemaforoStatus === 'concluido' || process.env.REACT_APP_STAGE === 'dev' ? (
                  <Receipt className="h-4 w-4" />
                ) : (
                  <Lock className="h-4 w-4 text-[hsl(215,16%,60%)]" />
                )}
                Saldo Remanescente
                {srSemaforoStatus !== 'concluido' && process.env.REACT_APP_STAGE !== 'dev' && (
                  <span className="ml-1 text-xs text-[hsl(215,16%,60%)]">(aguardando email SR)</span>
                )}
                {(srSemaforoStatus === 'concluido' || process.env.REACT_APP_STAGE === 'dev') && srData && srData.totalRegistros > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-xs min-w-[20px] text-center"
                  >
                    {srData.totalRegistros}
                  </Badge>
                )}
              </PageTabsTrigger>
            </PageTabsList>

            {/* Tab: Resumo */}
            <TabsContent value="resumo" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Resumo agregado por entregador
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Valor face somado de todas as operações da semana para cada entregador
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadResumo}
                    disabled={data.resumo.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.resumo.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">
                      Nenhum dado de resumo para este período.
                    </p>
                  ) : (
                    <DataTable
                      data={data.resumo}
                      columns={resumoColumns}
                      searchPlaceholder="Buscar por documento ou nome..."
                      pageSize={50}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Operações */}
            <TabsContent value="operacoes" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Operações individuais
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Cada linha representa uma antecipação realizada na semana
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadOperacoes}
                    disabled={data.operacoes.length === 0}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {data.operacoes.length === 0 ? (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10">
                      Nenhuma operação encontrada para este período.
                    </p>
                  ) : (
                    <DataTable
                      data={data.operacoes}
                      columns={operacoesColumns}
                      searchPlaceholder="Buscar por documento ou nome..."
                      pageSize={100}
                      compact
                      enableExport={false}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: NFS */}
            <TabsContent value="nfs" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Notas Fiscais de Serviço
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      NFSe emitidas na semana — antecipações (ANT) e saldos remanescentes (SR)
                    </p>
                  </div>
                  {/* ZIP assíncrono: passo 1 Solicitar → passo 2 Verificar / Baixar */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {!nfsZipJobKey ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRequestNFSZip}
                        disabled={!nfsData || (nfsData.totalAntecipacao + nfsData.totalSaldoRemanescente) === 0 || nfsZipLoading}
                        className="flex items-center gap-2"
                      >
                        {nfsZipLoading ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Solicitando...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            Solicitar ZIP
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-700 font-medium">
                          ZIP solicitado ✓
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCheckNFSZip}
                          disabled={nfsZipChecking}
                          className="flex items-center gap-2"
                        >
                          {nfsZipChecking ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Verificando...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              Verificar / Baixar
                            </>
                          )}
                        </Button>
                        <button
                          type="button"
                          onClick={nfsZipReset}
                          className="text-xs text-[hsl(215,16%,60%)] underline hover:text-[hsl(215,16%,40%)] transition-colors"
                        >
                          Nova solicitação
                        </button>
                      </div>
                    )}
                    {nfsZipError && (
                      <p className="text-xs text-red-600 max-w-xs text-right">{nfsZipError}</p>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {(nfsLoading || nfsFetching) && (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10 text-sm">
                      Carregando NFSe...
                    </p>
                  )}
                  {nfsError && !nfsLoading && (
                    <p className="text-center text-red-600 py-10 text-sm">{nfsError}</p>
                  )}
                  {!nfsLoading && !nfsError && nfsData && (
                    nfsData.totalAntecipacao + nfsData.totalSaldoRemanescente === 0 ? (
                      <p className="text-center text-[hsl(215,16%,47%)] py-10">
                        Nenhuma NFSe encontrada para este período.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                          <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-blue-50">
                                <FileDown className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Antecipações (ANT)</p>
                                <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">{nfsData.totalAntecipacao}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                          <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-violet-50">
                                <FileDown className="h-4 w-4 text-violet-600" />
                              </div>
                              <div>
                                <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Saldo Remanescente (SR)</p>
                                <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">{nfsData.totalSaldoRemanescente}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                          <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-red-50">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                              </div>
                              <div>
                                <p className="text-xs text-[hsl(215,16%,47%)] leading-none mb-1">Sem NFSe</p>
                                <p className="text-2xl font-bold text-[hsl(210,50%,10%)]">{nfsData.totalSemNFS}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Saldo Remanescente */}
            <TabsContent value="sr" className="mt-4">
              <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-[hsl(214,32%,91%)]">
                  <div>
                    <CardTitle className="text-base font-semibold text-[hsl(210,50%,10%)]">
                      Saldo Remanescente
                    </CardTitle>
                    <p className="text-xs text-[hsl(215,16%,47%)] mt-0.5">
                      Registros de SR da semana — dados do relatório de saldo remanescente
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadSR}
                    disabled={!srData || srData.totalRegistros === 0 || srLoading}
                    className="flex items-center gap-2 shrink-0"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  {(srLoading || srFetching) && (
                    <p className="text-center text-[hsl(215,16%,47%)] py-10 text-sm">
                      Carregando dados de SR...
                    </p>
                  )}
                  {srError && !srLoading && (
                    <p className="text-center text-red-600 py-10 text-sm">{srError}</p>
                  )}
                  {!srLoading && !srError && srData && (
                    srData.totalRegistros === 0 ? (
                      <p className="text-center text-[hsl(215,16%,47%)] py-10">
                        Nenhum registro de Saldo Remanescente para este período.
                      </p>
                    ) : (
                      <DataTable
                        data={srData.registros}
                        columns={srColumns}
                        searchPlaceholder="Buscar por documento ou nome..."
                        pageSize={100}
                        compact
                        enableExport={false}
                      />
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
